import React, { useEffect, useState } from 'react';
import API from '../services/api';

// Admin read-only detail popup for a new-client request: contact links the
// office can act on immediately (tel:/mailto:), the appointment + therapist,
// the decision + note, and the full Acuity intake answers.
const STATUS_LABEL = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  info_admin: 'Office follow-up',
  info_self: 'Therapist reaching out',
};
const NOTE_LABEL = {
  rejected: 'Reason for rejection',
  info_admin: 'Questions for the office',
};
// Intake keys already surfaced in the contact block — skip in the list below.
const CONTACT_KEYS = new Set(['firstName', 'lastName', 'email', 'phone']);

export default function NewClientDetailModal({ acuityId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    API.get(`/admin/new-client-requests/${acuityId}/detail`)
      .then((res) => { if (active) setDetail(res.data || null); })
      .catch(() => { if (active) setError("Couldn't load this request — try again in a moment."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [acuityId]);

  const intakeEntries = detail && detail.intake
    ? Object.entries(detail.intake).filter(([k, v]) => v && !CONTACT_KEYS.has(k))
    : [];

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0 }}>
            {detail ? detail.clientName || 'New client request' : 'New client request'}
          </h3>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close">&times;</button>
        </div>

        {loading && <div style={{ color: 'var(--text-muted)', padding: '12px 0' }}>Loading…</div>}
        {error && <div style={errorStyle}>{error}</div>}

        {detail && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={chipStyle}>{STATUS_LABEL[detail.status] || 'Pending'}</span>
              {detail.canceled && detail.status !== 'rejected' && (
                <span style={{ ...chipStyle, background: 'var(--muted-color)' }}>Appointment canceled</span>
              )}
              {detail.decidedAt && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  decided {new Date(detail.decidedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>

            {/* Contact block — the reason this popup exists */}
            <div style={contactBoxStyle}>
              {detail.clientPhone ? (
                <a href={`tel:${detail.clientPhone}`} style={contactLinkStyle}>📞 {detail.clientPhone}</a>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>📞 No phone on file</span>
              )}
              {detail.clientEmail ? (
                <a href={`mailto:${detail.clientEmail}`} style={contactLinkStyle}>✉️ {detail.clientEmail}</a>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>✉️ No email on file</span>
              )}
            </div>

            <div style={{ fontSize: 14, marginBottom: 12 }}>
              {detail.apptDatetime && (
                <div style={{ marginBottom: 4 }}>
                  <strong>Appointment:</strong>{' '}
                  {new Date(detail.apptDatetime).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}
                </div>
              )}
              <div><strong>Therapist:</strong> {detail.therapistName}</div>
            </div>

            {detail.note && (
              <div style={noteBoxStyle}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  {NOTE_LABEL[detail.status] || 'Note from therapist'}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{detail.note}</div>
              </div>
            )}

            <div style={{ fontWeight: 700, fontSize: 13, margin: '14px 0 6px', color: 'var(--text-strong)' }}>
              Acuity intake details
            </div>
            {detail.intake === null ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Couldn't reach Acuity for the intake answers right now — contact info above is from the last sync.
              </div>
            ) : intakeEntries.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No additional intake details.</div>
            ) : (
              <div style={intakeBoxStyle}>
                {intakeEntries.map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}>{labelize(k)}: </span>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function labelize(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const boxStyle = { background: 'var(--white)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 540, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 };
const closeBtnStyle = { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)' };
const chipStyle = { background: 'var(--primary-color)', color: '#fff', borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 600 };
const contactBoxStyle = { display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 12, marginBottom: 12 };
const contactLinkStyle = { color: 'var(--primary-color)', fontWeight: 700, fontSize: 16, textDecoration: 'none' };
const noteBoxStyle = { background: 'var(--secondary-color)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 12 };
const intakeBoxStyle = { background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 12, fontSize: 14, maxHeight: 240, overflowY: 'auto' };
const errorStyle = { background: '#f8d7da', color: '#721c24', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 14 };
